import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  Chip,
  Paper,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import toast from "react-hot-toast";
import BookingModal from "./BookingModal";
const API = process.env.REACT_APP_API_URL;

const PendingBookingsModal = ({ onClose, bedSeatId, type }) => {
  const [pendingBookings, setPendingBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchPendingBookings = async () => {
    try {
      const token = localStorage.getItem("token");
      const endpoint =
        type === "hostel"
          ? `${API}hostel/bookings/pending-list/?bed_id=${bedSeatId}`
          : `${API}library/bookings/pending-list/?seat_id=${bedSeatId}`;

      const res = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setPendingBookings(res.data);
    } catch (error) {
      toast.error("Failed to fetch pending bookings");
    }
  };

  useEffect(() => {
    fetchPendingBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bedSeatId, type]);

  const formatDateTime = (iso) => {
    const date = new Date(iso);
    const options = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    const time = date.toLocaleTimeString("en-IN", options);
    const formattedDate = date.toISOString().split("T")[0];
    return `${formattedDate} ${" / "} ${time}`;
  };

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        sx={{ zIndex: 50 }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 4,
            maxHeight: { xs: "90vh", md: "80vh" },
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#eff6ff",
            borderBottom: "1px solid #dbeafe",
            fontWeight: 600,
            fontSize: { xs: "1rem", md: "1.125rem" },
            color: "#1e40af",
            p: { xs: 2, md: 3 },
          }}
        >
          Students Applied for this {type === "hostel" ? "Bed" : "Seat"}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 }, overflowX: "auto" }}>
          {pendingBookings.length === 0 ? (
            <Box sx={{ textAlign: "center", py: { xs: 3, md: 4 } }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#6b7280",
                  fontSize: { xs: "0.875rem", md: "1rem" },
                }}
              >
                No students have applied yet.
              </Typography>
            </Box>
          ) : isMobile ? (
            // Mobile Card Layout
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                paddingTop: "10px",
              }}
            >
              {pendingBookings.map((b) => (
                <Card
                  key={b.booking_id}
                  sx={{
                    borderRadius: 2,
                    boxShadow: 1,
                    "&:hover": { boxShadow: 2 },
                    transition: "box-shadow 0.3s ease",
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: { xs: "0.875rem", md: "1rem" },
                        }}
                      >
                        #{b.booking_id}
                      </Typography>
                      <Chip
                        label={b.status === "pending" ? "Pending" : "Approved"}
                        size="small"
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          fontWeight: 500,
                          bgcolor:
                            b.status === "pending" ? "#fef3c7" : "#dcfce7",
                          color: b.status === "pending" ? "#d97706" : "#166534",
                          textTransform: "capitalize",
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: { xs: "0.875rem", md: "1rem" },
                          mb: 0.5,
                        }}
                      >
                        {b.student.full_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6b7280",
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                        }}
                      >
                        📅 {formatDateTime(b.created_at)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6b7280",
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                        }}
                      >
                        📍 {b.start_date}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6b7280",
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                        }}
                      >
                        💬 {b.purpose_of_joining}
                      </Typography>
                    </Box>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => setSelectedBookingId(b.booking_id)}
                      sx={{
                        borderColor: "#2563eb",
                        color: "#2563eb",
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                        "&:hover": {
                          bgcolor: "#eff6ff",
                          borderColor: "#1d4ed8",
                          color: "#1d4ed8",
                        },
                      }}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            // Desktop Table Layout
            <Paper
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: 1,
                paddingTop: "10px",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                        color: "#374151",
                      }}
                    >
                      Booking ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                        color: "#374151",
                      }}
                    >
                      Student Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                        color: "#374151",
                      }}
                    >
                      Booking Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                        color: "#374151",
                      }}
                    >
                      Start Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                        color: "#374151",
                      }}
                    >
                      Purpose
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                        color: "#374151",
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                        color: "#374151",
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingBookings.map((b) => (
                    <TableRow
                      key={b.booking_id}
                      sx={{
                        "&:hover": { bgcolor: "#f8fafc" },
                        "&:nth-of-type(even)": { bgcolor: "#fafafa" },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          color: "#374151",
                        }}
                      >
                        {b.booking_id}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          color: "#374151",
                          fontWeight: 500,
                        }}
                      >
                        {b.student.full_name}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          color: "#6b7280",
                        }}
                      >
                        {formatDateTime(b.created_at)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          color: "#6b7280",
                        }}
                      >
                        {b.start_date}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.875rem" },
                          color: "#6b7280",
                        }}
                      >
                        {b.purpose_of_joining}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            b.status === "pending" ? "Pending" : "Approved"
                          }
                          size="small"
                          sx={{
                            fontSize: { xs: "0.75rem", md: "0.875rem" },
                            fontWeight: 500,
                            bgcolor:
                              b.status === "pending" ? "#fef3c7" : "#dcfce7",
                            color:
                              b.status === "pending" ? "#d97706" : "#166534",
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => setSelectedBookingId(b.booking_id)}
                          sx={{
                            borderColor: "#2563eb",
                            color: "#2563eb",
                            fontSize: { xs: "0.75rem", md: "0.875rem" },
                            "&:hover": {
                              bgcolor: "#eff6ff",
                              borderColor: "#1d4ed8",
                              color: "#1d4ed8",
                            },
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: "#d1d5db",
              color: "#374151",
              fontSize: { xs: "0.875rem", md: "1rem" },
              "&:hover": { bgcolor: "#f9fafb" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {selectedBookingId && (
        <BookingModal
          bookingId={selectedBookingId}
          type={type}
          onClose={() => setSelectedBookingId(null)}
          onAction={() => window.location.reload()}
        />
      )}
    </>
  );
};

export default PendingBookingsModal;
